// The full lifecycle of a long-running bulk CSV job, shared by all three bulk
// pages: Issue Bulk Membership Passes (CBP), Re-Issue Bulk Membership Passes
// (RIP) and Issue Bulk Coupons to Members (BPA).
//
// All three POST a CSV and get back 202 + a job id, poll the same
// get-poll-status, and resume from the same get-active-jobs. The RULES they
// share are subtle enough that three copies would drift:
//
//   - The error file is revealed ONLY on the server's own verdict — status 1
//     with failed > 0, or status 2. Never on a network drop, stall or timeout,
//     where the server never reported a failure and the job is probably still
//     running.
//   - A refused duplicate submit (207 naming the running job) attaches to that
//     job rather than dead-ending on an error.
//   - Field names differ across the endpoints for the same values, so every
//     read is tolerant. A miss fails SILENTLY — an absent key renders a
//     confident 0 or a blank name, not an error.
//
// Pages supply their own process code and payload; everything else lives here.
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useUrlContext } from '../context/urlContext';
import { useAuth } from '../context/authContext';
import { errorContext } from './ErrorDisplay';
import { describeError, readServerMessage } from './errorUtils';
import useJobPoller, { JOB_FAILURE } from './useJobPoller';

// Some endpoints nest the body under `data`.
export function getPayloadRoot(payload) {
    if (payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object') {
        return payload.data;
    }
    return payload;
}

export function hasResponseError(payload) {
    const root = getPayloadRoot(payload);
    if (root == null || typeof root !== 'object') return false;
    const rawError = root.error ?? root.Error;
    return rawError === true || Number(rawError) === 1 || String(rawError).toLowerCase() === 'true';
}

// Present on a 202 (the job just started) and on a 207 refusal (the job already
// running). Either way it names the job to watch.
export function getJobId(payload) {
    const root = getPayloadRoot(payload);
    if (root == null || typeof root !== 'object') return null;
    const id = root.jobid ?? root.jobId ?? root.job_id ?? root.id;
    return (id === undefined || id === null || id === '') ? null : id;
}

// The uploaded CSV's name. Only the submitting tab knows it from the file
// picker; a refresh, or another browser, has to be told by the server.
export function getJobFileName(job) {
    if (job == null || typeof job !== 'object') return '';
    const name = job.uploadedCsv ?? job.uploadedCSV
        ?? job.fileName ?? job.filename ?? job.file_name ?? job.FileName;
    return typeof name === 'string' ? name : '';
}

export function getResponseErrorFile(payload) {
    const root = getPayloadRoot(payload);
    if (root == null || typeof root !== 'object') return '';
    const errorFile = root.errorfile ?? root.errorFile ?? root.ErrorFile;
    if (Array.isArray(errorFile)) return errorFile[0] || '';
    return errorFile || '';
}

export function getJobTotal(job) {
    if (job == null || typeof job !== 'object') return 0;
    return Number(job.total_records ?? job.totalCount) || 0;
}

export default function useBulkJob({ processCode, fallbackErrorMessage = 'The import failed.' } = {}) {
    const { UrlHelperObj } = useUrlContext();
    const { user } = useAuth();
    const { handleError, notify } = useContext(errorContext);

    const [submitting, setSubmitting] = useState(false);
    const [submittedFileName, setSubmittedFileName] = useState('');
    // The plain/error surface. Separate from `completion` so the green success
    // panel can never land on a failure.
    const [jobMessage, setJobMessage] = useState('');
    const [jobFailed, setJobFailed] = useState(false);
    const [completion, setCompletion] = useState(null);
    const [errorFileName, setErrorFileName] = useState('');

    // The error file NAME arrives before we know whether it is needed. Held in a
    // ref (it is not rendered) and copied into errorFileName only once the rules
    // above say the button should appear.
    const pendingErrorFileRef = useRef('');

    const buildStatusUrl = useCallback(
        (jobId) => UrlHelperObj.jobPollStatusUrl(jobId),
        [UrlHelperObj],
    );
    const { progress, isPolling, startPolling } = useJobPoller({ buildUrl: buildStatusUrl });

    const resetJob = useCallback(() => {
        setJobMessage('');
        setJobFailed(false);
        setErrorFileName('');
        setCompletion(null);
        // Or a second submit inherits the previous run's error file.
        pendingErrorFileRef.current = '';
    }, []);

    // Watches a job and wires its outcome into the page. Shared by the job we
    // just started, one adopted on mount, and one the server reports as already
    // running — so all three behave identically from here on.
    const watchJob = useCallback((jobId, initialProgress) => {
        startPolling(jobId, {
            initialProgress,
            onFinished: ({ job }) => {
                const succeeded = Number(job.success) || 0;
                const failed = Number(job.failed) || 0;
                const total = getJobTotal(job) || (succeeded + failed);
                // Deliberately NOT the server's message: on these endpoints it
                // is a transport line ("status retrieved") that says nothing
                // about the outcome. The completion panel states the result.
                setCompletion({ total, succeeded, failed });
                // Fill the filename if this page never had it (a resumed or
                // adopted job); never overwrite what the submitting tab knows.
                setSubmittedFileName((prev) => prev || getJobFileName(job));
                // status 1: offer the file only if something failed.
                if (failed > 0) {
                    setErrorFileName(getResponseErrorFile(job) || pendingErrorFileRef.current);
                }
                setSubmitting(false);
            },
            onFailed: ({ reason, message, job }) => {
                setJobMessage(message || fallbackErrorMessage);
                setJobFailed(true);
                // status 2 only — the server's own verdict, so an error file
                // exists. A network drop, stall or timeout means we merely lost
                // sight of a job that is probably still running; offering a file
                // there would imply a failure that has not happened.
                if (reason === JOB_FAILURE.JOB_ERROR) {
                    setErrorFileName(getResponseErrorFile(job) || pendingErrorFileRef.current);
                }
                setSubmitting(false);
            },
        });
    }, [startPolling, fallbackErrorMessage]);

    // Pick a running job back up on arrival. The import survives navigation —
    // only the browser's knowledge of it was lost — so without this, leaving the
    // page and returning means never learning how the run went.
    useEffect(() => {
        let cancelled = false;

        async function resumeActiveJob() {
            // Scoped to this club, so another club's run is never adopted here.
            if (!user?.issuerId || !processCode) return;
            try {
                const res = await axios.get(UrlHelperObj.activeJobsUrl(user.issuerId));
                if (cancelled) return;
                const root = getPayloadRoot(res.data) || {};
                // 207 with error:1 means "no active jobs" — a perfectly normal
                // state, NOT a failure. The jobs array is what we read; the
                // error flag is deliberately ignored.
                const jobs = Array.isArray(root.jobs) ? root.jobs : [];
                const mine = jobs.find(
                    (j) => String(j?.process).toUpperCase() === String(processCode).toUpperCase()
                );
                if (!mine || mine.id == null) return;

                setSubmitting(true);
                setSubmittedFileName(getJobFileName(mine));
                // Held for the end: whether it is offered still depends on how
                // the job finishes.
                pendingErrorFileRef.current = getResponseErrorFile(mine);
                watchJob(mine.id, {
                    totalCount: getJobTotal(mine),
                    success: Number(mine.success) || 0,
                    failed: Number(mine.failed) || 0,
                });
            } catch (err) {
                // Best-effort: if we cannot ask, a fresh submit still works. Do
                // not throw an error at someone who merely opened the page.
            }
        }

        resumeActiveJob();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.issuerId, processCode]);

    // POSTs the job and takes over from there. `localFileName` is the picker's
    // name, used until the server tells us its own.
    async function submitJob(url, formData, localFileName = '') {
        setSubmitting(true);
        resetJob();
        if (localFileName) setSubmittedFileName(localFileName);

        // Set when the POST hands back a job id, so `finally` knows not to clear
        // `submitting` — the work is still running.
        let pollingStarted = false;

        await axios.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then((response) => response.data)
            .then((results) => {
                if (hasResponseError(results)) {
                    // The server refuses a second submit while one is running for
                    // this club and names the running job. Not a dead end: attach
                    // to it and show its progress.
                    const runningJobId = getJobId(results);
                    if (runningJobId != null) {
                        pollingStarted = true;
                        setJobMessage(readServerMessage(results) || 'An import is already running.');
                        // Informational, not a failure — the progress panel below
                        // shows the job it is talking about.
                        setJobFailed(false);
                        watchJob(runningJobId);
                        return;
                    }

                    setJobMessage(readServerMessage(results) || fallbackErrorMessage);
                    setJobFailed(true);
                    setErrorFileName(getResponseErrorFile(results));
                    return;
                }

                // 202 + jobid: the import runs in the background. Keyed on the
                // presence of jobid rather than the status code.
                const jobId = getJobId(results);
                if (jobId != null) {
                    pollingStarted = true;
                    pendingErrorFileRef.current = getResponseErrorFile(results);
                    // Prefer the server's name: if it ever renames on upload, its
                    // name is the authoritative one.
                    const serverName = getJobFileName(results);
                    if (serverName) setSubmittedFileName(serverName);
                    watchJob(jobId);
                    return;
                }

                // A server that answered outright.
                setJobMessage(readServerMessage(results));
                setJobFailed(false);
            })
            .catch((err) => {
                // The duplicate refusal is 207, which axios RESOLVES — handled
                // above, never here. This is genuine transport / 4xx / 5xx.
                const body = err?.response?.data;
                const message = readServerMessage(body) || describeError(err);
                setJobMessage(message);
                setJobFailed(true);
                setErrorFileName(getResponseErrorFile(body));
                notify(describeError(err), 'error');
            })
            .finally(() => {
                // While polling, `submitting` must STAY true or the page
                // re-enables Submit mid-job and a second import can be fired.
                if (!pollingStarted) setSubmitting(false);
            });
    }

    async function downloadErrorFile(fileName) {
        if (!fileName) return;
        try {
            const response = await axios.get(UrlHelperObj.downloadCsvUrl(fileName), {
                responseType: 'text',
            });
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
            const link = document.createElement('a');
            const objectUrl = URL.createObjectURL(blob);
            link.href = objectUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            handleError(error);
        }
    }

    return {
        submitting,
        isPolling,
        progress,
        completion,
        jobMessage,
        jobFailed,
        errorFileName,
        submittedFileName,
        submitJob,
        resetJob,
        downloadErrorFile,
    };
}
